// Shared vocabulary and the pure logic of the Default Coverage Masters screen.
//
// Extracted for the same reason `buildUpdateInput` is on the opt-out side: these
// are the whole of the decisions, and a full render is not a reliable place to
// assert them.

import moment from 'moment';

export const MASTER_TYPE = {
  BUYER: 'MASTER_BUYER',
  SELLER: 'MASTER_SELLER',
  SELLER_PAID: 'MSTR_SELLER_PAID',
};

export const PARTY_SIDE_LABEL = {
  [MASTER_TYPE.BUYER]: 'Buyer side',
  [MASTER_TYPE.SELLER]: 'Seller side',
  [MASTER_TYPE.SELLER_PAID]: 'Seller-paid side',
};

export const STATUS = {
  RESOLVING: 'Active',
  SCHEDULED: 'Scheduled',
  NOT_RESOLVING: 'Not resolving',
  ENDED: 'Ended',
};

// Two causes, and the row cannot tell which: a product problem, or another master on the
// same side winning — the resolver takes ONE master per side (lowest product id, then
// lowest master id), so a second live master on a side never resolves.
export const NOT_RESOLVING_GUIDANCE = 'This master has not been end-dated, but it cannot '
  + 'resolve on a sale: either its product is inactive or is no longer on this side\'s default '
  + 'coverage allowlist, or another master on this side resolves instead — only one master per '
  + 'side resolves (the lowest product id, then the lowest master id). Nobody is being charged '
  + 'for it. End it, or fix the product / end the other master.';

// A future start is the one live-but-not-resolving case that is not a problem:
// APMS reports it the same way (not started => not resolving), so without this
// the row would tell the operator its product is inactive and to end it.
export const SCHEDULED_GUIDANCE = 'This master has not started yet, so nobody is being charged '
  + 'for it until its start date. It becomes Active then; if it does not, its product is '
  + 'inactive or is no longer on this side\'s default coverage allowlist.';

export const STALE_VERSION_HEADER = 'This master changed while you were editing it';
export const STALE_VERSION_GUIDANCE = 'Your change was not applied, and re-sending it never will '
  + 'be — someone else saved this master after you loaded it. Refresh the list and reapply.';

export const NO_MASTERS_HEADER = 'No default coverage is configured';
export const NO_MASTERS_GUIDANCE = 'No payer side has a master, so no sale receives a product '
  + 'by default and every opt-out row is currently inert. Add a master to start default coverage.';

export const MASTERS_ERROR_HEADER = 'Could not load default coverage masters';
export const MASTERS_ERROR_GUIDANCE = 'The list failed to load, so what is configured is '
  + 'unknown - this is not the same as nothing being configured. Retry; if it persists, check '
  + 'APMS before adding a master, or you may create a duplicate.';

export const NO_ELIGIBLE_PRODUCTS_GUIDANCE = 'The eligible product on this side already has '
  + 'an active master. End one before adding another, or switch sides.';

// Distinct from the above: this side has never had a master, which means nothing is on its
// dynamic_properties.defaultMaster allowlist yet (v2) — not "every product is taken". Telling the
// operator to "end one" here would be wrong: there is nothing to end. Rendered negative (red), not
// warning (orange), because there is no operator action on this screen that resolves it.
export const NOT_CONFIGURED_FOR_SIDE_GUIDANCE = 'No product on this side of default coverage is '
  + 'in allowlist yet, can not proceed. This side needs to be configured in apms db before it can '
  + 'carry one.';

export const CREATE_BLAST_RADIUS = 'Creating this master means every qualifying sale on this '
  + 'side will be covered with this product, unless that party has opted out of it.';

// A master's effective state. `endTs` alone is not enough: a master can be
// open-ended and still not resolve because its product was deactivated or
// recategorised, and that row would otherwise read as "Active" while covering
// nobody. `resolving` / `liveButNotResolving` are computed server-side against
// the resolver's own predicate so the two cannot disagree.
//
// The one split made here: APMS's liveButNotResolving is also true for a master
// whose start is still in the future (not started => not resolving), and that
// is scheduled, not broken. APMS judges "started" against UTC now on a
// zone-less LocalDateTime, so startTs is read as UTC here to match. `now` is
// injectable for tests.
export const startsInFuture = (master, now = moment()) => Boolean(
  master && master.startTs && moment.utc(master.startTs).isAfter(now)
);

export const statusOf = (master, now = moment()) => {
  if (!master) return STATUS.ENDED;
  if (master.resolving) return STATUS.RESOLVING;
  if (master.liveButNotResolving) {
    return startsInFuture(master, now) ? STATUS.SCHEDULED : STATUS.NOT_RESOLVING;
  }
  return STATUS.ENDED;
};

export const isEnded = master => statusOf(master) === STATUS.ENDED;

// Ended masters cannot be edited or re-ended: there is nothing to change that
// would have any effect, and re-ending would only move an end date that has
// already passed.
export const isEditable = master => !isEnded(master);

// Splits the flat list into the three payer sides it actually is. No priority
// any more (v2): a side realistically holds one master, so this is a grouping,
// not a ladder.
export const groupByPayerSide = (masters = []) => ({
  [MASTER_TYPE.BUYER]: masters.filter(m => m && m.masterType === MASTER_TYPE.BUYER),
  [MASTER_TYPE.SELLER]: masters.filter(m => m && m.masterType === MASTER_TYPE.SELLER),
  [MASTER_TYPE.SELLER_PAID]: masters.filter(m => m && m.masterType === MASTER_TYPE.SELLER_PAID),
});

// Which master is actually resolving on this side right now. `resolving` is
// computed server-side against the resolver's own predicate (started, not
// ended, product active and eligible) — not re-derived here, so the two cannot
// disagree. Normally at most one row on a side is resolving; if the data
// transiently holds two, the first is shown and is exactly the one the
// resolver itself would pick (see DefaultCoverageMaster's description).
export const effectiveMasterFor = (masters = []) => masters.find(m => m && m.resolving) || null;

// The next master due to start on this side, so a side with nothing resolving
// yet but something scheduled does not read as unconfigured.
export const scheduledMasterFor = (masters = [], now = moment()) => (
  masters
    .filter(m => m && statusOf(m, now) === STATUS.SCHEDULED)
    .sort((a, b) => moment.utc(a.startTs).valueOf() - moment.utc(b.startTs).valueOf())[0]
  || null
);

export const toProductOptions = (products = []) => (products || []).map(product => ({
  key: product.productId,
  value: product.productId,
  text: product.productName
    ? `${product.productCode} - ${product.productName}`
    : String(product.productCode || product.productId),
  description: product.categoryCode,
}));

// Three states that all present as an empty list and must not read the same -
// the same trap the opt-out picker hit. "Nothing is configured" is a claim about
// the data, so it may only be made once the query has actually come back.
export const hasNoMasters = (masters, loading, failed = false) => (
  !loading && !failed && Array.isArray(masters) && masters.length === 0
);

export const hasMastersError = (failed, loading) => Boolean(failed) && !loading;

export const isStaleVersionError = (errorCode, message) => (
  errorCode === 'STALE_VERSION'
  || (typeof message === 'string' && message.includes('changed by someone else'))
);
